import Preact, { Fragment, h } from "preact";
import { useState, useEffect, useRef } from "preact/hooks";
import "../../styles/widget.scss";

import WidgetLegacy from "./variants/WidgetLegacy";
import WidgetDefault from "./variants/WidgetDefault";
import WidgetDefault2 from "./variants/WidgetDefault2";
import WidgetGreenPan from "./variants/WidgetGreenPan";
import WidgetAsFee from "./variants/WidgetAsFee";

const Widget = ({
  parentRef,
  quote,
  payer,
  onOpenModalClick,
  status,
  mode,
  alignment = "right",
  variant = "default",
  logo,
  heading,
  subheading,
  primarycolor,
  secondarycolor,
  fontfamily,
}) => {
  const [toggle, setToggle] = useState(status);
  const isWidgetDefaultVariant = variant === "default";
  const widgetRef = useRef();

  useEffect(() => {
    const event = new CustomEvent("route:widget:rendered");
    const webComponent = document.querySelector("route-protect-widget");

    if (webComponent) {
      webComponent.dispatchEvent(event);
    }
  }, []);

  useEffect(() => {
    setToggle(status);
  }, [status]);

  const toggleHandler = () => {
    setToggle(toggle == "false" || toggle == undefined ? "true" : "false");
    const event = new CustomEvent("route:widget:toggle");
    const webComponent = document.querySelector("route-protect-widget");

    if (webComponent) {
      webComponent.dispatchEvent(event);
    }
  };

  const onKeyDown = (event) => {
    if (event.keyCode == 13) {
      event.preventDefault();
      onOpenModalClick();
    }
  };
  const onToggleKeyDown = (event) => {
    if (event.keyCode == 13) {
      event.preventDefault();
      toggleHandler();

      let el = event.target;
      const isAriaChecked =
        el.getAttribute("aria-checked") == "true" ? "false" : "true";
      el.setAttribute("aria-checked", isAriaChecked);
    }
  };
  const getMessageForVoiceOver = (toggled) => {
    let message = "";

    if (toggled) {
      message = "Disable Route Protection, Toggle is on";
    } else {
      message = "Enable Route Protection, Toggle is off";
    }

    return message;
  };

  const getWidth = () =>
    widgetRef.current.querySelector(".pw-container")
      ? widgetRef.current.querySelector(".pw-container").clientWidth
      : window.innerWidth;

  function useCurrentWidth() {
    const [classToAdd, setClassToAdd] = useState("");
    let resizeObserver;

    // // in this case useEffect will execute only once because
    // // it does not have any dependencies.
    useEffect(() => {
      const containerEl =
        widgetRef.current && widgetRef.current.querySelector(".pw-container")
          ? widgetRef.current.querySelector(".pw-container")
          : null;

      const throttle = (f, delay) => {
        let timer = 0;
        return function (...args) {
          clearTimeout(timer);
          timer = setTimeout(() => f.apply(this, args), delay);
        };
      };

      const resizeListener = () => {
        // change width from the state object
        let width = getWidth();
        switch (true) {
          case width <= 275 && width > 233:
            setClassToAdd("medium");
            break;
          case width <= 233 && width > 179:
            setClassToAdd("small");
            break;
          case width <= 179:
            setClassToAdd("extra-small");
            break;
          default:
            setClassToAdd("");
        }
      };

      try {
        resizeObserver = new ResizeObserver(throttle(resizeListener, 10));
      } catch (e) {
        resizeObserver = null;
      }

      // set resize listener
      resizeObserver && widgetRef.current && containerEl
        ? resizeObserver.observe(containerEl)
        : window.addEventListener("resize", resizeListener);

      // clean up function
      return () => {
        // remove resize listener
        resizeObserver && widgetRef.current && containerEl
          ? resizeObserver.unobserve(containerEl)
          : window.removeEventListener("resize", resizeListener);
      };
    }, []);

    return classToAdd;
  }

  let widgetWidthClass;

  try {
    widgetWidthClass = CSS.supports("container-type", "inline-size")
      ? ""
      : useCurrentWidth();
  } catch (e) {
    widgetWidthClass = useCurrentWidth();
  }

  return (
    <Fragment>
      {payer !== "no_coverage" && (
        <div
          className={`
            pw-route-protection 
            desktop-align-${alignment} 
            ${widgetWidthClass} 
            ${mode === "dark" && "dark-ui"} 
            ${variant === "green-pan" && "pw-variant-green-pan"}
          `}
          data-protection-widget
          data-testid="pw-widget"
          aria-label="route package protection selector element"
          ref={widgetRef}
        >
          {/*
          This logical fork is here to support the legacy widget, which should be unsupported soon.
          Once the legacy block is no longer supported, we can delete the logical check and only keep the row-based output found in the true condition
          Legacy CSS can also be deleted, which is scoped under .pw-container.legacy
          */}
          {variant === "legacy" ? (
            <>
              <WidgetLegacy
                quote={quote}
                payer={payer}
                onOpenModalClick={onOpenModalClick}
                status={status}
                mode={mode}
                alignment={alignment}
                variant={variant}
                onToggleKeyDown={onToggleKeyDown}
                toggle={toggle}
                toggleHandler={toggleHandler}
                getMessageForVoiceOver={getMessageForVoiceOver}
                onKeyDown={onKeyDown}
                isWidgetDefaultVariant={isWidgetDefaultVariant}
              />
            </>
          ) : variant === "default-2" ? (
            <>
              <WidgetDefault2
                quote={quote}
                payer={payer}
                onOpenModalClick={onOpenModalClick}
                status={status}
                mode={mode}
                alignment={alignment}
                variant={variant}
                onToggleKeyDown={onToggleKeyDown}
                toggle={toggle}
                toggleHandler={toggleHandler}
                getMessageForVoiceOver={getMessageForVoiceOver}
                onKeyDown={onKeyDown}
              />
            </>
          ) : variant === "green-pan" ? (
            <>
              <WidgetGreenPan
                quote={quote}
                payer={payer}
                onOpenModalClick={onOpenModalClick}
                status={status}
                mode={mode}
                alignment={alignment}
                variant={variant}
                onToggleKeyDown={onToggleKeyDown}
                toggle={toggle}
                toggleHandler={toggleHandler}
                getMessageForVoiceOver={getMessageForVoiceOver}
                onKeyDown={onKeyDown}
                logo={logo}
                heading={heading}
                subheading={subheading}
                primarycolor={primarycolor}
                secondarycolor={secondarycolor}
                fontfamily={fontfamily}
              />
            </>
          ) : variant === "fee" ? (
            <>
              <WidgetAsFee
                quote={quote}
                payer={payer}
                onOpenModalClick={onOpenModalClick}
                status={status}
                mode={mode}
                alignment={alignment}
                variant={variant}
                onToggleKeyDown={onToggleKeyDown}
                toggle={toggle}
                toggleHandler={toggleHandler}
                getMessageForVoiceOver={getMessageForVoiceOver}
                onKeyDown={onKeyDown}
                logo={logo}
                heading={heading}
                subheading={subheading}
                primarycolor={primarycolor}
                secondarycolor={secondarycolor}
                fontfamily={fontfamily}
              />
            </>
          ) : (
            <>
              <WidgetDefault
                quote={quote}
                payer={payer}
                onOpenModalClick={onOpenModalClick}
                status={status}
                mode={mode}
                alignment={alignment}
                variant={variant}
                onToggleKeyDown={onToggleKeyDown}
                toggle={toggle}
                toggleHandler={toggleHandler}
                getMessageForVoiceOver={getMessageForVoiceOver}
                onKeyDown={onKeyDown}
              />
            </>
          )}
        </div>
      )}
    </Fragment>
  );
};

export default Widget;
